import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  FileSearch, 
  Users, 
  Scale, 
  ChevronDown,
  ChevronUp,
  Check,
  Shield,
  Target,
  Award,
  Briefcase,
  Building2,
  Globe,
  Handshake,
  Phone
} from 'lucide-react';
import { services } from '../data/services';
import { homepageFaqs } from '../data/faqs';
import { SEO } from '../components/SEO';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from '../components/ui/carousel';

export const Home = ({ onOpenLeadPopup }) => {
  const [openFaq, setOpenFaq] = useState(null);

  const processSteps = [
    {
      number: '01',
      title: 'Submit Your Case',
      description: 'Share the details of your legal matter through our secure consultation form.',
      icon: FileSearch
    },
    {
      number: '02',
      title: 'Expert Consultation',
      description: 'Our experienced attorneys review your case and provide strategic guidance.',
      icon: Users
    },
    {
      number: '03',
      title: 'Legal Execution',
      description: 'We implement the optimal legal strategy to protect your interests.',
      icon: Scale
    }
  ];

  const whyChooseUs = [
    {
      icon: Shield,
      title: 'Trusted Expertise',
      description: 'Our attorneys bring deep expertise across corporate law, intellectual property, immigration, and compliance matters.'
    },
    {
      icon: Target,
      title: 'Results-Driven',
      description: 'We focus on delivering measurable outcomes that align with your business objectives and protect your interests.'
    },
    {
      icon: Handshake,
      title: 'Client Partnership',
      description: 'We work as an extension of your team, providing responsive counsel and strategic guidance when you need it.'
    },
    {
      icon: Award,
      title: 'Excellence Standard',
      description: 'Every matter receives meticulous attention to detail and the highest standards of legal professionalism.'
    }
  ];

  const areasWeServe = [
    { icon: Building2, title: 'Corporations', description: 'Fortune 500 to mid-market companies' },
    { icon: Briefcase, title: 'Startups', description: 'Emerging businesses and entrepreneurs' },
    { icon: Globe, title: 'International', description: 'Cross-border transactions and compliance' },
    { icon: Users, title: 'Non-Profits', description: 'Foundations and charitable organizations' }
  ];

  return (
    <div className="page-wrapper">
      <SEO 
        title="Home"
        description="Legal Emphasis provides strategic legal solutions for businesses across the United States. Expert counsel in corporate law, intellectual property, immigration, and business litigation."
        keywords="law firm, legal services, corporate law, business attorney, San Francisco lawyer, intellectual property, immigration law"
      />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-[90vh] flex items-center hero-bg"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/a1008463-c248-4e33-8176-88ea721473f0/images/63d734fe715a3e8261e015fe55d9a27f88dacb503f1dae8c21c36122bc6e7116.png')`
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="le-container relative z-10">
          <div className="max-w-3xl">
            <h1 className="le-heading-1 text-white mb-6 animate-fade-in-up">
              Strategic Legal Solutions That Protect What Matters Most
            </h1>
            <p className="text-lg md:text-xl text-gray-200 mb-8 animate-fade-in-up stagger-1 leading-relaxed">
              Trusted counsel for businesses across the United States. From corporate formation to complex litigation, we deliver results that matter.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up stagger-2">
              <button 
                onClick={onOpenLeadPopup}
                className="bg-white text-[#0A2A5E] px-8 py-4 font-semibold uppercase tracking-wider 
                           hover:bg-gray-100 transition-all duration-300 flex items-center justify-center gap-2 btn-lift"
                data-testid="hero-cta-btn"
              >
                Get Legal Help
                <ArrowRight size={18} />
              </button>
              <Link 
                to="/about"
                className="border-2 border-white text-white px-8 py-4 font-semibold uppercase tracking-wider 
                           hover:bg-white hover:text-[#0A2A5E] transition-all duration-300 text-center"
                data-testid="hero-learn-more"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About/Informative Section */}
      <section className="le-section bg-white" data-testid="about-section">
        <div className="le-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Content - Left Side */}
            <div>
              <span className="text-sm font-semibold text-[#0A2A5E] uppercase tracking-wider mb-4 block">
                About Legal Emphasis
              </span>
              <h2 className="le-heading-2 mb-6">
                A Full-Service Law Firm Dedicated to Your Success
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Legal Emphasis is a premier law firm headquartered in San Francisco, California, providing sophisticated legal counsel to businesses of all sizes across the United States.
                </p>
                <p>
                  Our attorneys bring extensive experience across corporate law, intellectual property, government contracting, business litigation, and immigration. We combine deep legal expertise with practical business insight to deliver solutions that drive real results.
                </p>
                <p>
                  We understand that legal matters are part of broader business objectives. That's why we work closely with each client to develop tailored strategies that address their specific needs and position them for long-term success.
                </p>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/about"
                  className="le-btn-primary"
                  data-testid="about-section-learn-more"
                >
                  Learn More About Us
                  <ArrowRight size={18} />
                </Link>
                <Link 
                  to="/attorneys"
                  className="le-btn-secondary"
                  data-testid="about-section-team"
                >
                  Meet Our Team
                </Link>
              </div>
            </div>

            {/* Image - Right Side */}
            <div className="relative">
              <div 
                className="w-full h-[400px] lg:h-[500px] bg-cover bg-center"
                style={{
                  backgroundImage: `url('https://images.unsplash.com/photo-1769740333462-9a63bfa914bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMHRlYW0lMjBjb2xsYWJvcmF0aW9uJTIwb2ZmaWNlJTIwdGFibGUlMjBkaXNjdXNzaW9uJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3Mzk0NDM0MHww&ixlib=rb-4.1.0&q=85')`
                }}
              ></div>
              {/* Decorative element */}
              <div className="absolute -bottom-6 -left-6 bg-[#0A2A5E] p-6 hidden lg:flex items-center gap-4">
                <Scale className="text-white" size={32} />
                <div className="text-white">
                  <p className="text-sm text-gray-200 font-semibold uppercase tracking-wider">Trusted Counsel</p>
                  <p className="text-xs text-gray-300">For Businesses Nationwide</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Slider Section */}
      <section className="le-section bg-[#F8F9FA]" data-testid="services-section">
        <div className="le-container">
          <div className="text-center mb-12">
            <span className="text-sm font-semibold text-[#0A2A5E] uppercase tracking-wider mb-4 block">
              Practice Areas
            </span>
            <h2 className="le-heading-2 mb-4">Our Legal Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive legal services tailored to meet the diverse needs of modern businesses.
            </p>
          </div>

          <div className="px-4 md:px-12">
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              className="w-full"
              data-testid="services-carousel"
            >
              <CarouselContent className="-ml-4">
                {services.map((service) => {
                  const IconComponent = service.icon;
                  return (
                    <CarouselItem 
                      key={service.id} 
                      className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3"
                    >
                      <Link 
                        to={`/services/${service.slug}`}
                        className="le-card service-card group h-full flex flex-col"
                        data-testid={`service-card-${service.slug}`}
                      >
                        <div className="service-icon">
                          <IconComponent size={28} strokeWidth={1.5} />
                        </div>
                        <h4 className="text-xl font-semibold text-[#0A2A5E] mb-3 group-hover:text-[#1a4a8e] transition-colors">
                          {service.title}
                        </h4>
                        <p className="text-gray-600 text-sm leading-relaxed flex-1">
                          {service.shortDescription}
                        </p>
                        <div className="mt-4 flex items-center text-[#0A2A5E] text-sm font-medium">
                          Learn More <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </Link>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
              <CarouselPrevious className="hidden md:flex -left-4 bg-[#0A2A5E] text-white border-none hover:bg-[#1a4a8e] hover:text-white" />
              <CarouselNext className="hidden md:flex -right-4 bg-[#0A2A5E] text-white border-none hover:bg-[#1a4a8e] hover:text-white" />
            </Carousel>
          </div>

          <div className="text-center mt-10">
            <button 
              onClick={onOpenLeadPopup}
              className="le-btn-primary btn-lift"
              data-testid="services-cta-btn"
            >
              Get Legal Help
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="le-section bg-white" data-testid="why-choose-section">
        <div className="le-container">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-[#0A2A5E] uppercase tracking-wider mb-4 block">
              Why Legal Emphasis
            </span>
            <h2 className="le-heading-2 mb-4">Why Clients Choose Us</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We combine legal excellence with a client-centered approach to deliver exceptional results.
            </p>
          </div>

          <div className="mobile-scroll-grid values-grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
            {whyChooseUs.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div 
                  key={index}
                  className="text-center group bg-white md:bg-transparent p-6 md:p-0 border border-gray-100 md:border-0"
                  data-testid={`why-choose-${index}`}
                >
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-[#F8F9FA] flex items-center justify-center mx-auto mb-4 md:mb-6 group-hover:bg-[#0A2A5E] transition-colors duration-300">
                    <IconComponent className="text-[#0A2A5E] group-hover:text-white transition-colors duration-300" size={28} strokeWidth={1.5} />
                  </div>
                  <h4 className="text-base md:text-lg font-semibold text-[#0A2A5E] mb-2 md:mb-3">{item.title}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Areas We Serve Section */}
      <section className="le-section bg-white" data-testid="areas-section">
        <div className="le-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left - Image */}
            <div className="relative order-2 lg:order-1 hidden lg:block">
              <div 
                className="w-full h-[350px] lg:h-[450px] bg-cover bg-center"
                style={{
                  backgroundImage: `url('https://images.unsplash.com/photo-1758413352731-d6f9ce3b59d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxjbGFzc2ljYWwlMjBhcmNoaXRlY3R1cmUlMjBpbnRlcmlvciUyMG1hcmJsZSUyMGhhbGwlMjBjZWlsaW5nfGVufDB8fHx8MTc3Mzk0Mzg2Nnww&ixlib=rb-4.1.0&q=85')`
                }}
              ></div>
            </div>

            {/* Right - Content */}
            <div className="order-1 lg:order-2">
              <span className="text-sm font-semibold text-[#0A2A5E] uppercase tracking-wider mb-4 block">
                Who We Serve
              </span>
              <h2 className="le-heading-2 mb-6">
                Serving Businesses Across All Industries
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                From Fortune 500 corporations to emerging startups, we provide strategic legal counsel to organizations at every stage of growth.
              </p>

              <div className="mobile-scroll-grid areas-grid grid-cols-1 sm:grid-cols-2">
                {areasWeServe.map((area, index) => {
                  const IconComponent = area.icon;
                  return (
                    <div 
                      key={index}
                      className="flex items-start gap-4 bg-white p-4 md:p-0 border border-gray-100 md:border-0"
                      data-testid={`area-${index}`}
                    >
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-[#F8F9FA] flex items-center justify-center flex-shrink-0">
                        <IconComponent className="text-[#0A2A5E]" size={20} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#0A2A5E] mb-1 text-sm md:text-base">{area.title}</h4>
                        <p className="text-gray-600 text-xs md:text-sm">{area.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="le-section bg-[#F8F9FA]" data-testid="process-section">
        <div className="le-container">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-sm font-semibold text-[#0A2A5E] uppercase tracking-wider mb-4 block">
              Our Process
            </span>
            <h2 className="le-heading-2 mb-4">How It Works</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              A straightforward process designed to get you the legal support you need.
            </p>
          </div>

          <div className="mobile-scroll-grid process-grid grid-cols-1 md:grid-cols-3">
            {processSteps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <div 
                  key={step.number}
                  className="text-center relative bg-white md:bg-transparent p-6 md:p-0"
                  data-testid={`process-step-${index + 1}`}
                >
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-[#0A2A5E] text-white flex items-center justify-center mx-auto mb-4 md:mb-6">
                    <IconComponent size={28} strokeWidth={1.5} />
                  </div>
                  <span className="text-4xl md:text-5xl font-bold text-[#0A2A5E]/10 absolute top-4 md:top-0 left-1/2 -translate-x-1/2 md:-translate-y-4">
                    {step.number}
                  </span>
                  <h3 className="text-lg md:text-xl font-semibold text-[#0A2A5E] mb-2 md:mb-3">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <button 
              onClick={onOpenLeadPopup}
              className="le-btn-primary btn-lift"
              data-testid="process-cta-btn"
            >
              Start Your Case
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="le-section bg-white" data-testid="faq-section">
        <div className="le-container">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-sm font-semibold text-[#0A2A5E] uppercase tracking-wider mb-4 block">
                FAQs
              </span>
              <h2 className="le-heading-2 mb-4">Frequently Asked Questions</h2>
              <p className="text-gray-600">
                Common questions about our legal services and processes.
              </p>
            </div>

            <div className="space-y-4">
              {homepageFaqs.map((faq) => (
                <div 
                  key={faq.id}
                  className="border border-[#E2E8F0]"
                  data-testid={`faq-item-${faq.id}`}
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-[#F8F9FA] transition-colors"
                    data-testid={`faq-toggle-${faq.id}`}
                  >
                    <span className="font-semibold text-[#0A2A5E] pr-8">{faq.question}</span>
                    {openFaq === faq.id ? (
                      <ChevronUp size={20} className="text-[#0A2A5E] flex-shrink-0" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  <div 
                    className={`overflow-hidden transition-all duration-300 ${
                      openFaq === faq.id ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    <p className="px-6 pb-6 text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section 
        className="relative py-24 md:py-32"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1681505526188-b05e68c77582?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbHMlMjBzaGFraW5nJTIwaGFuZHMlMjBkZWFsJTIwYWdyZWVtZW50JTIwd2lkZSUyMGFuZ2xlfGVufDB8fHx8MTc3Mzk0NTE2MHww&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
        data-testid="final-cta-section"
      >
        <div className="absolute inset-0 bg-[#0A2A5E]/80"></div>
        <div className="le-container relative z-10 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Schedule Your Consultation Today
          </h2>
          <p className="text-lg text-gray-200 mb-8 max-w-2xl mx-auto">
            Let our experienced attorneys guide you through your legal challenges with confidence.
          </p>
          <button 
            onClick={onOpenLeadPopup}
            className="bg-white text-[#0A2A5E] px-10 py-4 font-semibold uppercase tracking-wider 
                       hover:bg-gray-100 transition-all duration-300 inline-flex items-center justify-center gap-2 btn-lift"
            data-testid="final-cta-btn"
          >
            Get Legal Help
            <ArrowRight size={18} />
          </button>
        </div>
      </section>

    </div>
  );
};
